// =====================================================
// FORMULARIO DE STOCK AGRÍCOLA
// src/pages/Agro/StockAgricola/FormStockAgricola.js
// =====================================================

import React, { useState, useEffect, useRef } from "react"
import {
  Row,
  Col,
  Button,
  Input,
  FormGroup,
  Label,
  Spinner,
  Table,
} from "reactstrap"
import { Formik, Form, Field, FieldArray } from "formik"
import * as Yup from "yup"
import Flatpickr from "react-flatpickr"
import "flatpickr/dist/themes/material_blue.css"
import { Spanish } from "flatpickr/dist/l10n/es"

import NumberInput from "components/Input/NumberInput"
import AmountInput from "components/Input/AmountInput"
import {
  saveStockAgricola,
  updateStockAgricola,
  getCultivos,
  getTiposAlmacenamiento,
  getCampanas,
  getPrecioReferenciaStock,
} from "api/agro"
import toastHandler from "toastHandler"
import useAuthorization from "hooks/useAuthorization"

// cantidadQq y precioUsdTn se guardan como number | "" en Formik.
const StockAgricolaSchema = Yup.object().shape({
  idEstablecimiento: Yup.number().required("Seleccione establecimiento"),
  fechaStock: Yup.date().required("Requerido"),
  items: Yup.array()
    .of(
      Yup.object().shape({
        codigoCultivo: Yup.string().required("Requerido"),
        cantidadQq: Yup.number()
          .typeError("Debe ser un número válido")
          .required("Requerido")
          .min(0.001, "Debe ser mayor a 0"),
        precioUsdTn: Yup.mixed().test(
          "es-numero-o-vacio",
          "Debe ser un número válido",
          v =>
            v === "" ||
            v === null ||
            v === undefined ||
            (typeof v === "number" && !isNaN(v)),
        ),
      }),
    )
    .min(1, "Agregue al menos un item"),
})

const FormStockAgricola = ({
  establecimientos,
  onSuccess,
  onCancel,
  idEstablecimiento,
  stockAEditar,
}) => {
  // BDSJEA-208 hotfix: ver la nota equivalente en FormSiembra.js. Se usa el
  // mismo objeto que ya gatea "Registrar Stock" y Editar/Eliminar en
  // ActividadAgricola.js, en vez del flag global de ruta userStore.modificacion.
  const { puedeModificarObjeto } = useAuthorization()
  const modificacion = puedeModificarObjeto("/agro/establecimientos")
  const [cultivos, setCultivos] = useState([])
  const [tiposAlmacenamiento, setTiposAlmacenamiento] = useState([])
  const [campanas, setCampanas] = useState([])
  const [loading, setLoading] = useState(true)
  // Rastrea el origen del precio de cada fila:
  //   true     = autocargado desde valor_agro (puede refrescarse si cambia est/cultivo)
  //   "manual" = el usuario lo escribió (nunca se pisa)
  //   false/undefined = vacío
  const autocompletadoRef = useRef({})
  // BDSJEA-215: filas donde la búsqueda de precio no devolvió valor.
  const [sinPrecio, setSinPrecio] = useState({})

  const setSuccess = toastHandler(state => state.setSuccess)
  const setError = toastHandler(state => state.setError)

  useEffect(() => {
    loadDominios()
  }, [])

  const loadDominios = async () => {
    try {
      const [cultivosData, tiposData, campanasData] = await Promise.all([
        getCultivos(),
        getTiposAlmacenamiento(),
        getCampanas(),
      ])
      setCultivos(cultivosData)
      setTiposAlmacenamiento(tiposData)
      setCampanas(campanasData)
    } catch (error) {
      console.error("Error cargando dominios:", error)
    } finally {
      setLoading(false)
    }
  }

  // Valuación = cantidadQq × 0,1 × precioUsdTn (1 QQ = 0,1 tn), redondeado entero.
  const calcularValuacion = (cantidadQq, precioUsdTn) => {
    if (!cantidadQq || cantidadQq <= 0 || !precioUsdTn) return null
    return Math.round((cantidadQq * precioUsdTn) / 10)
  }

  // BDSJEA-215: al cambiar establecimiento o cultivo se limpia el precio y,
  // si la búsqueda no encuentra valor para la zona/cultivo, se marca la fila
  // para mostrar el avise rojo bajo el campo.
  const buscarPrecioReferencia = async (
    index,
    idEstablecimiento,
    codigoCultivo,
    setFieldValue,
  ) => {
    setFieldValue(`items.${index}.precioUsdTn`, "")
    setSinPrecio(prev => ({ ...prev, [index]: false }))
    autocompletadoRef.current[index] = false
    if (!idEstablecimiento || !codigoCultivo) return
    try {
      const resp = await getPrecioReferenciaStock(
        idEstablecimiento,
        codigoCultivo,
      )
      if (resp?.precioUsdTn != null && resp.precioUsdTn !== "") {
        setFieldValue(`items.${index}.precioUsdTn`, Number(resp.precioUsdTn))
        autocompletadoRef.current[index] = true
      } else {
        setSinPrecio(prev => ({ ...prev, [index]: true }))
      }
    } catch {
      setSinPrecio(prev => ({ ...prev, [index]: true }))
    }
  }

  const handleEstablecimientoChange = async (
    nuevoIdEstablecimiento,
    values,
    setFieldValue,
  ) => {
    setFieldValue("idEstablecimiento", nuevoIdEstablecimiento)
    setSinPrecio({})
    for (let i = 0; i < values.items.length; i++) {
      await buscarPrecioReferencia(
        i,
        nuevoIdEstablecimiento,
        values.items[i].codigoCultivo,
        setFieldValue,
      )
    }
  }

  // Al elegir cultivo, limpiar precio y autocargar desde valor_agro.
  const handleCultivoChange = async (
    index,
    codigoCultivo,
    values,
    setFieldValue,
  ) => {
    setFieldValue(`items.${index}.codigoCultivo`, codigoCultivo)
    await buscarPrecioReferencia(
      index,
      values.idEstablecimiento,
      codigoCultivo,
      setFieldValue,
    )
  }

  const handleSubmit = async (values, { setSubmitting, setTouched }) => {
    setTouched({ idEstablecimiento: true, fechaStock: true })
    if (!values.idEstablecimiento || !values.fechaStock) {
      setSubmitting(false)
      return
    }
    try {
      if (stockAEditar) {
        const item = values.items[0]
        await updateStockAgricola(stockAEditar.id, {
          idEstablecimiento: parseInt(values.idEstablecimiento),
          codigoCultivo: item.codigoCultivo,
          codigoTipoAlmacenamiento: item.codigoTipoAlmacenamiento || null,
          cantidadQq: item.cantidadQq || null,
          precioReferenciaKg: item.precioUsdTn ? item.precioUsdTn / 1000 : null,
          fechaStock: values.fechaStock,
          campania: item.campania || null,
        })
        setSuccess("Stock agrícola actualizado correctamente")
      } else {
        for (const item of values.items) {
          await saveStockAgricola({
            idEstablecimiento: parseInt(values.idEstablecimiento),
            codigoCultivo: item.codigoCultivo,
            codigoTipoAlmacenamiento: item.codigoTipoAlmacenamiento || null,
            cantidadQq: item.cantidadQq || null,
            // El backend almacena el precio en USD/kg; convertimos desde USD/tn.
            precioReferenciaKg: item.precioUsdTn
              ? item.precioUsdTn / 1000
              : null,
            fechaStock: values.fechaStock,
            campania: item.campania || null,
          })
        }
        setSuccess("Stock agrícola registrado correctamente")
      }
      onSuccess && onSuccess()
    } catch (error) {
      setError("Error al guardar: " + error.message)
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner color="primary" />
      </div>
    )
  }

  return (
    <Formik
      initialValues={
        stockAEditar
          ? {
              idEstablecimiento: stockAEditar.idEstablecimiento || "",
              fechaStock: stockAEditar.fechaStock
                ? new Date(stockAEditar.fechaStock + "T00:00:00")
                : new Date(),
              items: [
                {
                  codigoCultivo: stockAEditar.codigoCultivo || "",
                  codigoTipoAlmacenamiento:
                    stockAEditar.codigoTipoAlmacenamiento || "",
                  cantidadQq: stockAEditar.cantidadQq || "",
                  precioUsdTn: stockAEditar.precioReferenciaKg
                    ? Number(stockAEditar.precioReferenciaKg) * 1000
                    : "",
                  campania: stockAEditar.campania || "",
                },
              ],
            }
          : {
              idEstablecimiento: idEstablecimiento || "",
              fechaStock: new Date(),
              items: [
                {
                  codigoCultivo: "",
                  codigoTipoAlmacenamiento: "",
                  cantidadQq: "",
                  precioUsdTn: "",
                  campania: "",
                },
              ],
            }
      }
      validationSchema={StockAgricolaSchema}
      onSubmit={handleSubmit}
    >
      {({ values, errors, touched, isSubmitting, setFieldValue }) => (
        <Form>
          <Row className="mb-4">
            <Col md={6}>
              <FormGroup>
                <Label>Establecimiento *</Label>
                <Field name="idEstablecimiento">
                  {({ field, form }) => (
                    <Input
                      {...field}
                      type="select"
                      invalid={
                        form.touched.idEstablecimiento &&
                        !form.values.idEstablecimiento
                      }
                      onChange={e =>
                        handleEstablecimientoChange(
                          e.target.value,
                          form.values,
                          form.setFieldValue,
                        )
                      }
                    >
                      <option value="">Seleccione...</option>
                      {establecimientos.map(est => (
                        <option key={est.id} value={est.id}>
                          {est.nombre}
                        </option>
                      ))}
                    </Input>
                  )}
                </Field>
                {/* BDSJEA-215: la zona resuelta del establecimiento. El precio
                    de referencia (BDSJEA-189) sale de esa zona, asi que el
                    analista necesita verla al elegir el campo. */}
                {values.idEstablecimiento &&
                  (() => {
                    const est = establecimientos.find(
                      e => String(e.id) === String(values.idEstablecimiento),
                    )
                    if (!est) return null
                    if (est.nombreZona && est.zonaConfiable) {
                      return (
                        <small className="text-muted d-block">
                          <i className="mdi mdi-map-marker-outline me-1" />
                          Zona: {est.nombreZona}
                        </small>
                      )
                    }
                    if (est.nombreZona) {
                      return (
                        <small className="text-warning d-block">
                          <i className="mdi mdi-alert-outline me-1" />
                          Zona: {est.nombreZona} (default provincial
                          {est.motivoZona ? ` — ${est.motivoZona}` : ""})
                        </small>
                      )
                    }
                    return (
                      <small className="text-muted d-block">
                        <i className="mdi mdi-help-circle-outline me-1" />
                        Zona no resuelta
                        {est.motivoZona ? ` — ${est.motivoZona}` : ""}
                      </small>
                    )
                  })()}
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label>Fecha de Stock *</Label>
                <Flatpickr
                  className={`form-control ${
                    touched.fechaStock && !values.fechaStock ? "is-invalid" : ""
                  }`}
                  value={values.fechaStock}
                  onChange={([date]) => setFieldValue("fechaStock", date)}
                  options={{ dateFormat: "d/m/Y", locale: Spanish }}
                />
              </FormGroup>
            </Col>
          </Row>

          <h6 className="mb-3">Detalle de Stock</h6>

          <FieldArray name="items">
            {({ push, remove }) => (
              <>
                <Table responsive bordered size="sm">
                  <thead className="table-light">
                    <tr>
                      <th className="text-center">Cultivo</th>
                      <th width="150" className="text-center">
                        Almacenamiento
                      </th>
                      <th width="120" className="text-center">
                        Cantidad (QQ)
                      </th>
                      <th width="140" className="text-center">
                        Precio (USD/tn)
                      </th>
                      <th width="150" className="text-center">
                        Valuación (USD)
                      </th>
                      <th width="130" className="text-center">
                        Campaña{" "}
                        <small className="text-muted fw-normal">
                          (Opcional)
                        </small>
                      </th>
                      <th width="50" className="text-center"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {values.items.map((item, index) => {
                      const valuacion = calcularValuacion(
                        item.cantidadQq,
                        item.precioUsdTn,
                      )

                      return (
                        <tr key={index}>
                          <td>
                            <Input
                              type="select"
                              bsSize="sm"
                              value={item.codigoCultivo}
                              onChange={e =>
                                handleCultivoChange(
                                  index,
                                  e.target.value,
                                  values,
                                  setFieldValue,
                                )
                              }
                            >
                              <option value="">Seleccione...</option>
                              {cultivos.map(c => (
                                <option key={c.codigo} value={c.codigo}>
                                  {c.nombre}
                                </option>
                              ))}
                            </Input>
                          </td>
                          <td>
                            <Field
                              name={`items.${index}.codigoTipoAlmacenamiento`}
                              as={Input}
                              type="select"
                              bsSize="sm"
                            >
                              <option value="">Sin tipo</option>
                              {tiposAlmacenamiento.map(t => (
                                <option key={t.codigo} value={t.codigo}>
                                  {t.descripcion}
                                </option>
                              ))}
                            </Field>
                          </td>
                          <td>
                            <NumberInput
                              name={`items.${index}.cantidadQq`}
                              value={item.cantidadQq}
                              onChange={v =>
                                setFieldValue(`items.${index}.cantidadQq`, v)
                              }
                              nroDecimales={0}
                              min={0}
                              className="form-control-sm"
                            />
                          </td>
                          <td>
                            <AmountInput
                              name={`items.${index}.precioUsdTn`}
                              value={item.precioUsdTn}
                              onChange={v => {
                                // onChange solo se dispara al perder foco (NumberInput).
                                // Escribir a mano quita el aviso de "sin precio".
                                autocompletadoRef.current[index] =
                                  v !== "" ? "manual" : false
                                if (v !== "")
                                  setSinPrecio(prev => ({
                                    ...prev,
                                    [index]: false,
                                  }))
                                setFieldValue(`items.${index}.precioUsdTn`, v)
                              }}
                              currency=""
                              nroDecimales={2}
                              placeholder="Auto"
                              className="form-control-sm"
                            />
                            {sinPrecio[index] && (
                              <small className="text-danger d-block mt-1">
                                No se encontró precio para el cultivo/zona del
                                establecimiento
                              </small>
                            )}
                          </td>
                          <td>
                            <AmountInput
                              value={valuacion ?? ""}
                              onChange={() => {}}
                              currency=""
                              nroDecimales={0}
                              disabled
                              className={`form-control-sm fw-bold${valuacion != null ? " text-success border-success" : ""}`}
                            />
                          </td>
                          <td>
                            <Field
                              name={`items.${index}.campania`}
                              as={Input}
                              type="select"
                              bsSize="sm"
                            >
                              <option value="">Seleccionar...</option>
                              {campanas.map(c => (
                                <option key={c.codigo} value={c.codigo}>
                                  {c.codigo}
                                </option>
                              ))}
                            </Field>
                          </td>
                          <td
                            className="text-center"
                            style={{
                              width: 50,
                              minWidth: 50,
                              maxWidth: 50,
                              verticalAlign: "middle",
                            }}
                          >
                            {values.items.length > 1 && (
                              <Button
                                color="danger"
                                size="sm"
                                outline
                                style={{ width: "100%", padding: "0.2rem" }}
                                onClick={() => {
                                  remove(index)
                                  setSinPrecio(prev => {
                                    const next = {}
                                    Object.keys(prev).forEach(k => {
                                      const ki = Number(k)
                                      if (ki < index) next[ki] = prev[k]
                                      else if (ki > index)
                                        next[ki - 1] = prev[k]
                                    })
                                    return next
                                  })
                                }}
                              >
                                <i className="mdi mdi-trash-can" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="table-light">
                      <td colSpan={2}>
                        {!stockAEditar && (
                          <Button
                            color="success"
                            size="sm"
                            outline
                            onClick={() => {
                              push({
                                codigoCultivo: "",
                                codigoTipoAlmacenamiento: "",
                                cantidadQq: "",
                                precioUsdTn: "",
                                campania: "",
                              })
                            }}
                          >
                            <i className="mdi mdi-plus me-1" />
                            Agregar Línea
                          </Button>
                        )}
                      </td>
                      <td>
                        <NumberInput
                          value={values.items.reduce(
                            (acc, i) => acc + (i.cantidadQq || 0),
                            0,
                          )}
                          onChange={() => {}}
                          nroDecimales={0}
                          disabled
                          className="form-control-sm fw-bold"
                        />
                      </td>
                      <td></td>
                      <td>
                        <AmountInput
                          value={values.items.reduce((acc, i) => {
                            const v = calcularValuacion(
                              i.cantidadQq,
                              i.precioUsdTn,
                            )
                            return acc + (v || 0)
                          }, 0)}
                          onChange={() => {}}
                          currency=""
                          nroDecimales={0}
                          disabled
                          className="form-control-sm fw-bold text-success border-success"
                        />
                      </td>
                      <td colSpan={2}></td>
                    </tr>
                  </tfoot>
                </Table>
              </>
            )}
          </FieldArray>

          {errors.items && typeof errors.items === "string" && (
            <div className="text-danger small mb-3">{errors.items}</div>
          )}

          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button color="secondary" onClick={onCancel}>
              Cancelar
            </Button>
            {modificacion && (
              <Button color="primary" type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <Spinner size="sm" />
                ) : stockAEditar ? (
                  "Guardar Cambios"
                ) : (
                  "Guardar Stock"
                )}
              </Button>
            )}
          </div>
        </Form>
      )}
    </Formik>
  )
}

export default FormStockAgricola
